import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Grid,
} from "@mui/material";

import {
  DataObject as DataIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import { ScrapedUsersViewer } from "./DataImportViewers";
import DataImportSection from "./DataImport/DataImport";
import CustomPagination from "views/client/pagination";
import colors from "colors/color";
import CategoryDiagram from "./CategoryDiagram";
import SimpleAlert from "components/Dialog/SimpleAlert";
import { trackFileImport } from 'utils/analytics';

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const UploadBox = styled(Paper)(({ theme }) => ({
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    transform: "scale(1.02)",
  },
}));

const AnimatedDataIcon = styled(DataIcon)(({ theme }) => ({
  fontSize: 60,
  color: theme.palette.primary.main,
  animation: "$float 3s ease-in-out infinite",
  "@keyframes float": {
    "0%": {
      transform: "translateY(0px)",
    },
    "50%": {
      transform: "translateY(-20px)",
    },
    "100%": {
      transform: "translateY(0px)",
    },
  },
}));
const StyledTypography = styled(Typography)(() => ({
  fontFamily: "Poppins",
  fontWeight: 400,
  fontSize: "16px",
  color: "#000000",
}));
export default function DataImport() {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [showImportProgress, setShowImportProgress] = useState(false); // Add dynamic progress state
  const [importFileCount, setImportFileCount] = useState(0); // Track file count
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(0); // Track remaining time
  const [categories, setCategories] = useState([]);
  const [categoriesRec , setCategoriesRec]= useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const apiUrl = process.env.REACT_APP_API_URL;
  const [showDataScrapingAnimation, setShowDataScrapingAnimation] =
    useState(false);

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [isCodeLoading, setIsCodeLoading] = useState(false);
  const [isScrapingStarted, setIsScrapingStarted] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // 10 PDFs per page
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [openalert, setOpenalert]= useState(false);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("");



  const getUserIdFromAccessToken = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      console.log("Access token:", decodedToken);
      return decodedToken.user_id;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  };

  const parseJwt = (token) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    console.log("payload:", jsonPayload);

    return JSON.parse(jsonPayload);
  };
  const recruiterId = getUserIdFromAccessToken();
  const handleStartScraping = async () => {
    // console.log("Starting scraping for email:", email);

    console.log("Recruiter ID:", recruiterId);
    if (!recruiterId) {
      console.error("Failed to get recruiter ID");
      return;
    }

    setIsCodeLoading(true);

    try {
      const response = await fetch(
        `${apiUrl}ai/start_scraping2/${recruiterId}/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Scraping API response:", data);

      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Error starting scraping:", error);
      setIsCodeLoading(false);
      // Handle error (e.g., show an error message to the user)
    }
  };
  function openAuthPopup() {
    const authUrl =
      "https://login.microsoftonline.com/common/oauth2/deviceauth";
    const width = window.screen.width / 3;
    const height = window.screen.height;
    const left = window.screen.width - width;
    const top = 0;

    window.open(
      authUrl,
      "authPopup",
      `width=${width},height=${height},top=${top},left=${left}`
    );
  }
  const code_outlook = async () => {
    console.log("start to fetch code:");
    try {
      const response = await fetch(
        `${apiUrl}ai/code_outlook/${getUserIdFromAccessToken()}/`
      );
      const result = await response.json();
      console.log("Fetched code:", result.user_code);
      setCode(result.user_code);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsCodeLoading(false);
    }
  };
  const handleCloseAlert = () => {
    setOpenalert(false);
  };

  const handleCopy = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(code)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
        });
    } else {
      console.error("Clipboard API not available");
    }
  };

  // Add this useEffect to log state changes
  useEffect(() => {
    console.log("isScrapingStarted:", isScrapingStarted);
    console.log("isCodeLoading:", isCodeLoading);
    console.log("code:", code);
    fetchCategories();
    // code_outlook();
  }, [isScrapingStarted, isCodeLoading, code]);

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(
        `${apiUrl}recruiters/categories/${recruiterId}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch categories");
      const data = await response.json();
      console.log("Categories:", data);
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError(t("failedToFetchCategories"));
    }
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value); // Update selectedCategory
    setIsDropdownOpen(false); // Close the dropdown after selection
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      const response = await fetch(
        `${apiUrl}recruiters/categories/create/${recruiterId}/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: JSON.stringify({ name: newCategory.trim() }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        console.log("Error response:", errorData); // Debugging

        // Handle the error message
        if (errorData.error) {
          setError(errorData.error); // Set the error message directly
        } else {
          setError("Failed to add category. Please try again.");
        }
        return;
      }
      const addedCategory = await response.json();
      setCategories([...categories, addedCategory]);
      setNewCategory("");
    } catch (error) {
      console.error("Error adding category:", error);
      setError(t("failedToAddCategory"));
    }
  };
   const fetchCategoriesRecruiter = async () => {
    try {
      const response = await fetch(
        `${apiUrl}authentication/categories/recruiter/${recruiterId}/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch categories");
      const data = await response.json();
      
      // Sort categories to ensure 'autre' is displayed last
      if (data && data.categories && Array.isArray(data.categories)) {
        const sortedCategories = [...data.categories].sort((a, b) => {
          // Check if either category is 'autre' (case insensitive)
          const aIsAutre = (a.name && a.name.toLowerCase() === 'autre') || 
                           (a.name_fr && a.name_fr.toLowerCase() === 'autre');
          const bIsAutre = (b.name && b.name.toLowerCase() === 'autre') || 
                           (b.name_fr && b.name_fr.toLowerCase() === 'autre');
          
          // If a is 'autre', move it to the end
          if (aIsAutre) return 1;
          // If b is 'autre', keep it at the end
          if (bIsAutre) return -1;
          // Otherwise maintain original order
          return 0;
        });
        
        setCategoriesRec({
          ...data,
          categories: sortedCategories
        });
      } else {
        setCategoriesRec(data);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };
  useEffect(() => {
    fetchCategoriesRecruiter();
  }, [refreshTrigger]); // Add refreshTrigger as dependency
  const deleteCategory = async (categoryId) => {
    const response = await fetch(
      `${apiUrl}recruiters/categories/${recruiterId}/${categoryId}/`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      }
    );
    fetchCategories();
    if (!response.ok) throw new Error("Failed to delete category");
    return true;
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);

    const selectedFiles = Array.from(event.dataTransfer.files);
    processFiles(selectedFiles);
  };

  const handleFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);
    processFiles(selectedFiles);
    // Reset the input value to allow selecting the same file again
    event.target.value = "";
  };

  const processFiles = (selectedFiles) => {
    let newFiles = [];
    let errors = [];

    selectedFiles.forEach((file) => {
      if (file.type === "application/pdf") {
        const isDuplicate = files.some(
          (existingFile) => existingFile.name === file.name
        );

        if (!isDuplicate) {
          newFiles.push({
            id: Date.now() + Math.random(),
            name: file.name,
            type: file.type,
            size: (file.size / 1024).toFixed(2) + " KB",
            uploadDate: new Date().toLocaleDateString(),
            fileObject: file,
          });
          
          // Track file import by recruiter
          trackFileImport('resume', file.name, file.size);
        } else {
          errors.push(`${file.name} (Duplicate)`);
        }
      } else {
        errors.push(file.name);
      }
    });

    if (errors.length > 0) {
      setError(
        t("someFilesNotUploaded", {
          files: errors.join(", "),
        })
      );
    } else {
      setError("");
    }

    setFiles([...files, ...newFiles]);
  };
  const uploadFiles = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    
    // Show dynamic progress bar
    setShowImportProgress(true);
    setImportFileCount(files.length);
    
    // Calculate dynamic duration: fileCount * 20 seconds
    const totalDuration = files.length * 20; // seconds
    const progressIncrement = 100 / totalDuration; // percentage per second
    setEstimatedTimeRemaining(totalDuration);
    
    let elapsedTime = 0;
    let dynamicInterval;

    try {
      const formData = new FormData();
      if (!selectedCategoryNew) {
        setOpenalert(true);
        setIsUploading(false);
        setShowImportProgress(false);
        return;
      }
      if (!selectedSubCategory) {
        console.log('sub category not provided!');
      } else {
        formData.append("sub_category", selectedSubCategory.id);
      }

      files.forEach((file) => {
        formData.append("files[]", file.fileObject);
      });

      formData.append("category", selectedCategoryNew.id);
      
      // Start dynamic progress simulation
      dynamicInterval = setInterval(() => {
        elapsedTime += 1;
        setEstimatedTimeRemaining(totalDuration - elapsedTime);
        
        setUploadProgress(prev => {
          if (prev >= 100) {
            clearInterval(dynamicInterval);
            return 100;
          }
          return prev + progressIncrement; // Dynamic increment based on file count
        });
      }, 1000); // Update every second
      
      const response = await fetch(
        `${apiUrl}recruiters/cvupload/${getUserIdFromAccessToken()}/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t("uploadFailed"));
      }

      const data = await response.json();
      console.log("Upload successful:", data);
      
      // Wait for progress to complete before proceeding
      const waitForProgress = () => {
        return new Promise((resolve) => {
          const checkProgress = () => {
            if (uploadProgress >= 100 || elapsedTime >= totalDuration) {
              resolve();
            } else {
              setTimeout(checkProgress, 500);
            }
          };
          checkProgress();
        });
      };
      
      await waitForProgress();
      
      // Clear interval if still running
      if (dynamicInterval) {
        clearInterval(dynamicInterval);
      }
      
      // Ensure progress reaches 100%
      setUploadProgress(100);
      setEstimatedTimeRemaining(0);
      
      // Clear files and reset error on success
      setFiles([]);
      setError("");
      setSelectedCategory("");

      // Reset file input
      const fileInput = document.getElementById("file-upload");
      if (fileInput) {
        fileInput.value = "";
      }

      // Hide progress bar after completion
      setTimeout(() => {
        setShowImportProgress(false);
        // Trigger data refresh after progress completes
        setRefreshTrigger((prev) => prev + 1);
        // Refresh category data for the chart
        fetchCategoriesRecruiter();
        
        // Show success animation
        setShowSuccessAnimation(true);
        setTimeout(() => {
          setShowSuccessAnimation(false);
          setShowDataScrapingAnimation(true);
          // Clear selected categories when data scraping animation starts
          setSelectedCategoryNew(null);
          setSelectedSubCategory(null);
          setSelectedCategoryFilter("");
          setTimeout(() => {
            setShowDataScrapingAnimation(false);
          }, 5000);
        }, 2000);
      }, 500);
      
    } catch (error) {
      console.error("Error uploading files:", error);
      setError(error.message || t("failedToUploadFiles"));
      
      // Clear interval on error
      if (dynamicInterval) {
        clearInterval(dynamicInterval);
      }
      setShowImportProgress(false);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const removeFile = (id) => {
    setFiles(files.filter((file) => file.id !== id));
  };

  const removeAllFiles = () => {
    setFiles([]);
    setError("");
  };
  const getPaginatedFiles = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return files.slice(startIndex, endIndex);
  };
  const PaginationControls = () => {
    const handleChangePage = (event, newPage) => {
      setCurrentPage(newPage + 1); // CustomPagination uses 0-based indexing, but currentPage is 1-based
    };

    const handleChangeRowsPerPage = (event) => {
      setItemsPerPage(parseInt(event.target.value, 10));
      setCurrentPage(1); // Reset to first page when changing items per page
    };

    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
        <CustomPagination
          page={currentPage - 1} // Convert to 0-based indexing for CustomPagination
          handleChangePage={handleChangePage}
          rowsPerPage={itemsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          count={files.length}
        />
      </Box>
    );
  };

  const [selectedCategoryNew, setSelectedCategoryNew] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  const handleCategorySelection = (category) => {
    setSelectedCategoryNew(category);
    console.log('Selected category in parent:', category);
  };

  const handleSubCategorySelection = (subcategory) => {
    setSelectedSubCategory(subcategory);
    console.log('Selected subcategory in parent:', subcategory);
  };

  // Handle category selection from CategoryDiagram chart
  const handleCategoryFilterSelection = (category) => {
    console.log("Category selected from chart:", category);
    // If category.id is null, clear the filter
    const newCategoryId = category.id || "";
    console.log("Setting selectedCategoryFilter to:", newCategoryId);
    setSelectedCategoryFilter(newCategoryId);
  };
  return (
    <Box sx={{ display: "flex", maxWidth: "100%", mx: "auto", mt: 4 }}>
      <Grid container sx={{ position: "relative" }}>
        <Grid item xs={12}>
          <DataImportSection
            PaginationControls={PaginationControls}
            AnimatedDataIcon={AnimatedDataIcon}
            VisuallyHiddenInput={VisuallyHiddenInput}
            UploadBox={UploadBox}
            itemsPerPage={itemsPerPage}
            categories={categories}
            selectedCategory={selectedCategory}
            newCategory={newCategory}
            isDropdownOpen={isDropdownOpen}
            files={files}
            dragging={dragging}
            error={error}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
            showSuccessAnimation={showSuccessAnimation}
            showDataScrapingAnimation={showDataScrapingAnimation}
            showImportProgress={showImportProgress}
            importFileCount={importFileCount}
            estimatedTimeRemaining={estimatedTimeRemaining}
            handleCategoryChange={handleCategoryChange}
            setIsDropdownOpen={setIsDropdownOpen}
            deleteCategory={deleteCategory}
            setNewCategory={setNewCategory}
            handleAddCategory={handleAddCategory}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleFileDrop={handleFileDrop}
            handleFileUpload={handleFileUpload}
            setError={setError}
            removeAllFiles={removeAllFiles}
            uploadFiles={uploadFiles}
            removeFile={removeFile}
            getPaginatedFiles={getPaginatedFiles}
            onCategorySelect={handleCategorySelection}
            onSubCategorySelect={handleSubCategorySelection}
          />
        </Grid>
        <SimpleAlert
           open={openalert}
           onClose={handleCloseAlert}
           text="Veuillez sélectionner une catégorie avant de télécharger."
           type = 'warning'
        />
        
        {/* Data Viewers Section with Toggle Button */}
        <Grid item xs={12} md={12}>
          <Box sx={{ width: "100%", p: 0, px: 2 }}>
            {/* Header with Title only */}
            {/* <Box sx={{ 
              display: "flex", 
              justifyContent: "flex-start", 
              alignItems: "center", 
              mb: 1,
              px: 0
            }}>
              <StyledTypography  align="start">
                {t("dataImport.dataManagement")}
              </StyledTypography>
            </Box> */}

            {/* Enhanced Category Diagram - Full Width */}
            <Box sx={{ 
              width: "100%", 
              mb: 0, // Increased spacing before next component
              mx: 0, // Remove any horizontal margin
              px: 0  // Remove padding that could constrain width
            }}>
              <CategoryDiagram 
                data={categoriesRec} 
                onCategoryClick={handleCategoryFilterSelection}
                selectedCategoryId={selectedCategoryFilter}
              />
            </Box>

            {/* Scraped Users Viewer with top spacing */}
            <Box sx={{ 
              mt: 0, // Additional top margin for separation
              pt: 1, // Top padding
              borderTop: "1px solid #e2e8f0", // Subtle separator line
            }}>
              <ScrapedUsersViewer
                recruiterId={recruiterId}
                apiUrl={process.env.REACT_APP_API_URL}
                refreshTrigger={refreshTrigger}
                selectedCategoryFilter={selectedCategoryFilter}
                onCategoryFilterChange={setSelectedCategoryFilter}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
